import type { ShelemView } from "@bing/engine-shelem";
import { useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { SuitGlyph } from "../components/SuitGlyph";
import { atY } from "../game/felt-geometry";
import { HAKEM_NOTICE_PILL, HAKEM_NOTICE_TEXT, HAKEM_NOTICE_WEIGHT } from "../game/hakem-chip";
import { faDigits } from "../i18n/digits";
import { fa } from "../i18n/fa";
import { CENTRE_GAP_MAX, FeltPill, type auctionCentrePlacement } from "./shelem-auction";

/* ------------------------------------------------------------------ *
 * شلم's CONTRACT PARTS (P1 Task 5) — what the table says once the auction has made a contract:
 *   · the other seats' line as the حاکم takes the کف, «{name} زمین را برداشت» (`KafPickupLine`);
 *   · the حاکم's first card naming trump, «حکم: ♠ — {name}» (`TrumpAnnounce`, P1-5) — the FIRST
 *     place the suit appears;
 *   · the bottom bar (`ContractHud`, P1-6; card-67 B3; card-98 item 8): the contract, OUR points and
 *     THEIR points this hand, one line, fixed slots — and the match target only where the header's
 *     score box has no room for it.
 * The suit's pip is the table's to draw (`SuitGlyph`, its colour by suit) — the announce takes it as a
 * node, so no suit is drawn twice (the trump SIGN is `game/trump-sign.tsx`, at the felt's top-left).
 * ------------------------------------------------------------------ */

/** The other seats' line as the حاکم takes the کف — the felt's statement pill, where the auction's
 *  «بالاترین: …» stood (`auctionCentrePlacement`): the name (`dir="auto"`, cut at its end), then
 *  «زمین را برداشت». A statement; the حاکم itself never reads it (its own order is in its strip). */
export function KafPickupLine({
  name,
  placement,
}: {
  name: string;
  placement: ReturnType<typeof auctionCentrePlacement>;
}): ReactNode {
  return (
    <FeltPill placement={placement} testId="shelem-kaf-pickup">
      <span
        dir="auto"
        className="m-0 min-w-0 truncate font-body-md text-body-md font-bold text-secondary"
      >
        {name}
      </span>
      <span className="m-0 shrink-0 whitespace-nowrap font-body-md text-body-md text-on-surface">
        {fa.shelem.tookKaf}
      </span>
    </FeltPill>
  );
}

/** The trump call's place at the felt's foot (`TrumpAnnounce`), its air under the first card, its floor. */
export const ANNOUNCE_BOTTOM = 24;
const ANNOUNCE_AIR = 4;
const ANNOUNCE_FLOOR = 4;

/**
 * «حکم: ♠ — {name}» — حکم's gold notice pill (`HakemNoticePill`'s own classes, shared since this
 * task), the suit's pip where the crown sits there. WHERE nothing else is at the first card: the
 * felt holds one card, the حاکم's, in front of the حاکم — so the pill sits at the felt's foot when
 * another seat led (your slot below the centre is empty), and in the gap under your partner's column
 * when YOU did (`top`, the auction centre's floor). No wider than the gap between the side columns;
 * the name is cut at its end. An entrance with no fill (`pop-in-glow`, the app's own keyframe).
 */
export function TrumpAnnounce({
  pip,
  name,
  at,
  top,
}: {
  /** The trump's pip, as the table draws it on the gold. */
  pip: ReactNode;
  name: string;
  at: "top" | "bottom";
  /** `at === "top"`: the felt-relative top (px) under your partner's column. */
  top: string;
}): ReactNode {
  // AT THE FELT'S FOOT, CLEAR OF THE FIRST CARD (Task 9b): 24 px up — where a short felt under a text
  // zoom puts the حاکم's card lower than the pill's top (13.9 px into it at 320×568 ×1.5, 18.9 at
  // 300×667 ×1.5, 2.2 at 300×667 ×1.3: the card's rank under the gold), the pill sits lower, its top
  // ANNOUNCE_AIR under the lowest trick card, and never nearer the felt's edge than ANNOUNCE_FLOOR.
  // Measured off the laid-out boxes before the first paint (unlaid-out: today's 24).
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [bottom, setBottom] = useState(ANNOUNCE_BOTTOM);
  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    const felt = wrap?.parentElement;
    const pill = wrap?.firstElementChild;
    if (at !== "bottom" || !wrap || !felt || !pill) return undefined;
    const measure = (): void => {
      // the pill's LAID-OUT height (its pop-in scales the box: lane 2 left it 1.6 px on the card),
      // the cards' painted boxes
      const pillH = pill instanceof HTMLElement ? pill.offsetHeight : 0;
      const cards = [...felt.querySelectorAll('[aria-label="trick"] .card')].map(
        (c) => c.getBoundingClientRect().bottom,
      );
      if (!(pillH > 0) || cards.length === 0) return;
      const clear = felt.getBoundingClientRect().bottom - Math.max(...cards) - ANNOUNCE_AIR - pillH;
      setBottom(clear < ANNOUNCE_BOTTOM ? Math.max(ANNOUNCE_FLOOR, clear) : ANNOUNCE_BOTTOM);
    };
    measure();
    // a card still landing is measured again where it comes to rest
    felt.addEventListener("animationend", measure, true);
    felt.addEventListener("transitionend", measure, true);
    return () => {
      felt.removeEventListener("animationend", measure, true);
      felt.removeEventListener("transitionend", measure, true);
    };
  }, [at]);
  const place: CSSProperties = at === "top" ? atY(top) : { bottom };
  return (
    <div
      ref={wrapRef}
      className="pointer-events-none absolute inset-x-0 z-30 flex justify-center"
      data-announce-at={at}
      style={place}
    >
      <span
        data-testid="shelem-trump-announce"
        role="status"
        className={`${HAKEM_NOTICE_PILL} min-w-0`}
        style={{ maxWidth: CENTRE_GAP_MAX, animation: "pop-in-glow .35s ease" }}
      >
        <span
          className={`${HAKEM_NOTICE_TEXT} shrink-0 whitespace-nowrap`}
          style={{ fontWeight: HAKEM_NOTICE_WEIGHT }}
        >
          {fa.shelem.trumpIs}
        </span>
        <span className="flex shrink-0 items-center leading-none" style={{ fontSize: 14 }}>
          {pip}
        </span>
        <span
          aria-hidden="true"
          className={`${HAKEM_NOTICE_TEXT} shrink-0`}
          style={{ fontWeight: HAKEM_NOTICE_WEIGHT }}
        >
          —
        </span>
        <span
          dir="auto"
          className={`${HAKEM_NOTICE_TEXT} min-w-0 truncate`}
          style={{ fontWeight: HAKEM_NOTICE_WEIGHT }}
        >
          {name}
        </span>
      </span>
    </div>
  );
}

/** THE BOTTOM BAR'S VALUE BOXES, in EM of their own size (the score box's way, `ShelemTable`'s
 *  SCORE_EM: Vazirmatn's digits are not tabular — ۳ is 0.679 em at 700, ۱ 0.309): each just over the
 *  widest value it can hold, off the font's `hmtx` (`shelem-layout.test.tsx` §4b re-derives both) —
 *    the contract  «شلم» (a call) 1.7598 em; the widest bid, «۱۳۵», 1.5972;
 *    our points    «۱۳۵» 1.5972 — the widest multiple of 5 a hand can show (card points come in 5s and
 *                  10s, a trick is 5; the کف's points are added only once the hand is over) — and
 *                  THEIR points the same (card-98 item 8).
 *  So a value never moves the bar as it changes, and the bar is ONE width through a hand. */
export const HUD_EM = { contract: "1.76em", points: "1.6em" } as const;

/** The dot by the contract (card-98 item 8, the operator: «a small dot or emoji by the contract»): a
 *  CSS disc in em, so it grows with the text under a WebView zoom — an emoji draws differently on
 *  every host (and as tofu where no emoji font is installed). */
const HUD_DOT_EM = "0.5em";

/** THE BAR'S WORDS in em of its 11 px bold — Vazirmatn 700's SHAPED glyphs off its `hmtx` (each just
 *  over; `shelem-layout.test.tsx` §4b re-derives them): an upper bound of what the line lays out. */
export const HUD_LABEL_EM = {
  contract: 2.75, // «قرارداد» 2.7407
  ours: 3.24, // «امتیاز ما» 3.2300
  theirs: 4.75, // «امتیاز حریف» 4.7485
  oursShort: 0.82, // «ما» 0.8179
  theirsShort: 2.34, // «حریف» 2.3364
} as const;
/** The bar's own px around its words, never zoomed: its `px-2.5` twice, four `gap-x-2.5` between its
 *  five parts, two 1 px rules, and each slot's `gap-1`s — two in the contract's, one in each points'. */
const HUD_CHROME_PX = 2 * 10 + 4 * 10 + 2 + 4 * 4;
/** Your panel's `px-3` either side of the bar. */
const PANEL_PAD_PX = 2 * 12;
const HUD_PX = 11;

/** The bar's ONE line at a text `zoom`, full («امتیاز ما» / «امتیاز حریف») or short («ما» / «حریف»). */
export function hudLineWidth(short: boolean, zoom: number): number {
  const em =
    parseFloat(HUD_DOT_EM) +
    HUD_LABEL_EM.contract +
    parseFloat(HUD_EM.contract) +
    (short
      ? HUD_LABEL_EM.oursShort + HUD_LABEL_EM.theirsShort
      : HUD_LABEL_EM.ours + HUD_LABEL_EM.theirs) +
    2 * parseFloat(HUD_EM.points);
  return HUD_CHROME_PX + em * HUD_PX * zoom;
}

/**
 * THE BAR'S SHORT FORM (card-98 item 8, the operator: «short form» — where the full line does not
 * fit): «ما ۱۰ · حریف ۶۵», the score box's own words, on a table `width` px wide at a text `zoom` —
 * phones of 360 px and under at ×1.5, Telegram Desktop's 256 px. Off the table's width and the zoom
 * alone, as the score box's forms are: ONE form per screen and zoom, never coming and going in a hand.
 * Full until laid out.
 */
export function hudShortForm(width: number | null, zoom: number): boolean {
  return width !== null && hudLineWidth(false, zoom) > width - PANEL_PAD_PX;
}

/**
 * THE CONTRACT HUD — the bottom bar (P1-6; card-67 B3; card-98 item 8), ABOVE your strip, where the
 * bidding row stood during the auction (the strip and the timer in it never move: the panel grows
 * upward, the felt gives the room). The operator (card-67 B3): «Bottom bar: drop what's already shown
 * elsewhere — the trump sign, who the حاکم is (marked above their head), and the 660/1200 target (at
 * the top). Show only the contract and our points, neatly, in fixed places that don't shift — one line
 * if it fits.» — and (card-98 item 8): «add the opponents' points to the same line as the contract and
 * our points, with a small dot or emoji by the contract. Each of the three keeps its own fixed colour
 * and fixed place.» So ONE line of fixed slots, centred:
 *   the contract   a dot, «قرارداد» and its value — the bid, or «شلم» for a call — in ONE colour,
 *                  gold, whoever holds it; the DOT alone wears the حاکم's team colour (yours green,
 *                  the opponents' red: whose contract it is — card-67's contract wore it whole);
 *   our points     «امتیاز ما» and YOUR team's points taken this hand (the engine's `pointsTaken` —
 *                  card points + 5 a trick, the کف excluded until the hand ends — AS COLLECTED,
 *                  `points`), green. The header's box shows the MATCH totals («ما» / «حریف»), so the
 *                  bar's «ours» is this hand's, the contract's race;
 *   their points   «امتیاز حریف» and the OTHER team's, the same way, red — the header's «حریف» colour;
 *                  where the full line does not fit (`hudShortForm`), the two read «ما» / «حریف»;
 *   the target     «تا ۶۶۰» — ONLY where the header's box draws no target (its tight form, `showTarget`:
 *                  320 px under a 130 % zoom and the like, where the box cannot hold it beside «شلم»)
 *                  — the bar carried it at every size before, and the target must stay on screen. It
 *                  may wrap under the line there; the form is fixed per screen and zoom, so it never
 *                  comes and goes in a hand.
 * Every value sits in a FIXED em box (`HUD_EM`), centred; the labels never change — nothing shifts as
 * the points grow, and the bar is one width through the hand. 11 px bold on `leading-tight`.
 * The trump's suit is the felt's top-left sign now (`game/trump-sign.tsx`), and the حاکم wears its chip
 * over its avatar.
 */
export function ContractHud({
  view,
  points,
  showTarget = false,
  short = false,
}: {
  view: ShelemView;
  /** Points taken per team AS COLLECTED (P1 Task 6, `pointsCollected`): the frame's `pointsTaken` less
   *  the trick the felt still holds — the HUD moves with the sweep, never before it. */
  points: readonly number[];
  /** The header's score box draws no target (its tight form): the bar carries it. */
  showTarget?: boolean;
  /** The full line does not fit (`hudShortForm`): the points' words are «ما» / «حریف». */
  short?: boolean;
}): ReactNode {
  const declarer = view.declarer;
  if (declarer === null || (view.phase !== "declaring" && view.phase !== "playing")) return null;
  const myTeam = view.you % 2;
  const ours = declarer % 2 === myTeam;
  const rule = <span aria-hidden="true" className="m-0 h-3 w-px shrink-0 bg-outline-variant" />;
  return (
    <div
      data-testid="shelem-contract-hud"
      role="group"
      aria-label={fa.shelem.contractHud}
      className="flex flex-wrap items-center justify-center gap-x-2.5 gap-y-0.5 rounded-xl bg-surface-container-highest px-2.5 py-1 text-[11px] font-bold leading-tight"
      style={{ boxShadow: "inset 0 2px 6px rgba(0,0,0,.4)" }}
    >
      <span
        data-hud="contract"
        className="flex shrink-0 items-center gap-1 whitespace-nowrap text-secondary"
      >
        <span
          aria-hidden="true"
          data-hud-dot={ours ? "ours" : "theirs"}
          className={`m-0 inline-block shrink-0 rounded-full ${ours ? "bg-primary" : "bg-error"}`}
          style={{ width: HUD_DOT_EM, height: HUD_DOT_EM }}
        />
        <span className="m-0">{fa.shelem.contract}</span>
        <span className="m-0 inline-block text-center" style={{ width: HUD_EM.contract }}>
          {view.called ? fa.shelem.title : faDigits(view.bid || view.highBid)}
        </span>
      </span>
      {rule}
      <span
        data-hud="ours"
        className="flex shrink-0 items-center gap-1 whitespace-nowrap text-primary"
      >
        <span className="m-0">{short ? fa.shelem.us : fa.shelem.ourPoints}</span>
        <span className="m-0 inline-block text-center" style={{ width: HUD_EM.points }}>
          {faDigits(points[myTeam] ?? 0)}
        </span>
      </span>
      {rule}
      <span
        data-hud="theirs"
        className="flex shrink-0 items-center gap-1 whitespace-nowrap text-error"
      >
        <span className="m-0">{short ? fa.shelem.them : fa.shelem.theirPoints}</span>
        <span className="m-0 inline-block text-center" style={{ width: HUD_EM.points }}>
          {faDigits(points[1 - myTeam] ?? 0)}
        </span>
      </span>
      {showTarget ? (
        // its rule INSIDE its slot: where the line cannot hold it, the two wrap together — never a
        // rule left at the first line's end
        <span
          data-hud="target"
          className="flex shrink-0 items-center gap-2.5 whitespace-nowrap text-on-surface-variant"
        >
          {rule}
          <span className="m-0">{`${fa.shelem.toTarget} ${faDigits(view.targetScore)}`}</span>
        </span>
      ) : null}
    </div>
  );
}

const SUIT_RED = new Set(["hearts", "diamonds"]);
/**
 * The trump's pip on the announce's gold, drawn as inline SVG. ⚠️ NOT the Unicode characters ♠♥♦♣: no
 * font this app ships can render them (see `SuitGlyph`), which left the trump display blank on devices
 * without a system suit glyph. حکم's own pair on the gold (`--c-suit-red-on-gold` / `--c-ink-on-gold`).
 * (Its dark-surface form was the contract HUD's pip until card-67 B3 moved the suit to the felt's trump
 * sign — no caller since, so it went: the B+C review.)
 */
export function trumpPip(suit: string): ReactNode {
  const red = SUIT_RED.has(suit);
  return (
    <span
      className="m-0"
      style={{ color: red ? "rgb(var(--c-suit-red-on-gold))" : "rgb(var(--c-ink-on-gold))" }}
    >
      <SuitGlyph suit={suit} />
    </span>
  );
}
